import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Eye, Trash, RotateCcw } from "lucide-react";

import useFetch from "../../hooks/useFetch";
import api from "../../api/axios";
import ProductVariantsModal from "../../components/ProductVariantsModal";
import SearchBar from "../../components/SearchBar";
import LoadingOverlay from "../../components/LoadingOverlay";
import Pagination from "../../components/Pagination";

function Products() {
  const {
    data: products,
    loading,
    refetch: fetchProducts,
  } = useFetch("/products?includeInactive=true");


  const {
    data: categories,
  } = useFetch("/categories");

  // estados UI
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const ITEMS_PER_PAGE = 50;
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedCategory]);

  const handleToggleStatus = async (product) => {
    const isActivating = !product.active;
    const message = isActivating
      ? "¿Deseas activar este producto?"
      : "¿Estás seguro de que deseas desactivar este producto?";

    if (!window.confirm(message)) return;

    try {
      await api.put(`/products/${product.id}/status`, { active: isActivating });
      await fetchProducts();
    } catch (error) {
      console.error("Error al cambiar estado del producto:", error);
    }
  };


  const openVariantsModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const closeVariantsModal = () => {
    setSelectedProduct(null);
    setShowModal(false);
  };

  const filteredProducts = (products || [])
    .filter((product) => {
      const matchesName = product.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "" ||
        product.category_id === parseInt(selectedCategory);

      return matchesName && matchesCategory;
    })
    .sort((a, b) => Number(b.active) - Number(a.active));

  // paginación
  const pageCount = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const currentProducts = filteredProducts.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  // exports
  const handleExportExcel = async () => {
    try {
      const res = await api.get("/export/products/excel", {
        params: {
          categoryId: selectedCategory || undefined,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "productos.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const res = await api.get("/export/products/pdf", {
        params: {
          categoryId: selectedCategory || undefined,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "productos.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <h2 className="text-3xl font-semibold text-gray-800">Productos</h2>

        <div className="flex items-center gap-3 flex-nowrap">
          <div className="w-64">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar producto..."
            />
          </div>

          <button
            onClick={handleExportPDF}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
          >
            PDF
          </button>

          <button
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Excel
          </button>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border rounded-lg px-3 py-2 shadow-sm"
          >
            <option value="">Todas las categorías</option>
            {(categories || []).map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <Link
            to="/products/create"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={18} />
            Nuevo Producto
          </Link>
        </div>
      </div>

      {/* TABLA */}
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-center text-sm uppercase">
              <th className="px-6 py-3">Estado</th>
              <th className="px-6 py-3">Nombre</th>
              <th className="px-6 py-3">Categoría</th>
              <th className="px-6 py-3">Cantidad</th>
              <th className="px-6 py-3 w-36">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {currentProducts.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-6 text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              currentProducts.map((product, index) => (
                <tr
                  key={product.id}
                  className={`border-t transition-colors ${!product.active
                    ? "bg-gray-200 opacity-60"
                    : index % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                    }`}
                >

                  {/* ✅ COLUMNA ESTADO */}
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {product.status !== "none" && (
                        <span
                          className={`w-3 h-3 rounded-full
            ${product.status === "red" ? "bg-red-600" : ""}
            ${product.status === "yellow" ? "bg-yellow-400" : ""}
            ${product.status === "green" ? "bg-green-500" : ""}
            ${product.status === "blue" ? "bg-blue-500" : ""}
          `}
                          title={
                            product.status === "red"
                              ? "Crítico / vencido"
                              : product.status === "yellow"
                                ? "Próximo a vencer"
                                : product.status === "green"
                                  ? "Vigente"
                                  : product.status === "blue"
                                    ? "Sin vencimiento"
                                    : ""
                          }
                        />
                      )}
                    </div>
                  </td>

                  {/* ✅ NOMBRE */}
                  <td className="px-6 py-4">
                    {product.name}
                  </td>

                  {/* ✅ CATEGORÍA */}
                  <td className="px-6 py-4">
                    {product.category?.name || "Sin categoría"}
                  </td>

                  {/* ✅ STOCK */}
                  <td className="px-6 py-4">
                    {product.stock}
                  </td>

                  {/* ✅ ACCIONES */}
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => openVariantsModal(product)}
                        className="bg-blue-500 text-white p-2 rounded-lg"
                      >
                        <Eye size={16} />
                      </button>

                      <Link
                        to={`/products/edit/${product.id}`}
                        className="bg-yellow-400 text-white p-2 rounded-lg"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </Link>

                      <button
                        onClick={() => handleToggleStatus(product)}
                        className={`p-2 rounded-lg text-white ${product.active
                          ? "bg-red-500 hover:bg-red-600"
                          : "bg-blue-500 hover:bg-blue-600"
                          }`}
                        title={product.active ? "Desactivar producto" : "Activar producto"}
                      >
                        {product.active ? <Trash size={16} /> : <RotateCcw size={16} />}
                      </button>
                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>

        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            currentItemsCount={currentProducts.length}
            totalItemsCount={filteredProducts.length}
          />
        )}
      </div>

      {showModal && selectedProduct && (
        <ProductVariantsModal
          product={selectedProduct}
          onClose={closeVariantsModal}
        />
      )}
    </div>
  );
}

export default Products;
